import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  CircularProgress,
  Chip,
  Divider,
} from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { useApolloClient, useQuery } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import {
  SEND_OR_SCHEDULE_REPORT,
  OR_SCHEDULE_REPORT_SEND_STATUS,
  SendOrScheduleReportData,
  SendOrScheduleReportVars,
  ReportSendStatus,
  OrScheduleReportSendStatusData,
  OrScheduleReportSendStatusVars,
} from '@/graph/group-performance';
import { orScheduleAnalyticsConfigQuery } from '@/graph/dashboard';
import { organizationUserOptions } from '@/graph/users';
import ReportRecipients from '../../sensor/ReportRecipients';

interface SendOrScheduleReportDialogProps {
  open: boolean;
  onClose: () => void;
  reportDate: string; // YYYY-MM-DD
}

const SendOrScheduleReportDialog: FC<SendOrScheduleReportDialogProps> = ({ open, onClose, reportDate }) => {
  const client = useApolloClient();
  const toaster = useToaster();

  const [recipientMode, setRecipientMode] = useState<'configured' | 'custom'>('configured');
  const [customRecipientIds, setCustomRecipientIds] = useState<number[]>([]);
  const [sending, setSending] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [sendStatus, setSendStatus] = useState<ReportSendStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: configData } = useQuery(orScheduleAnalyticsConfigQuery, {
    fetchPolicy: 'cache-first',
    skip: !open,
  });

  const { data: usersData } = useQuery(organizationUserOptions, {
    fetchPolicy: 'cache-first',
    skip: !open,
  });

  useEffect(
    () => () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    },
    []
  );

  useEffect(() => {
    if (open) {
      setRecipientMode('configured');
      setCustomRecipientIds([]);
      setSending(false);
      setRunId(null);
      setSendStatus(null);
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [open]);

  const configuredRecipientIds: number[] = configData?.orScheduleAnalyticsConfiguration?.dailyReportRecipients ?? [];

  const usersMap: Record<number, { id: number; name: string; email: string }> = {};
  if (usersData?.users) {
    for (const u of usersData.users) {
      usersMap[u.id] = u;
    }
  }

  const configuredRecipientNames = configuredRecipientIds.map((id: number) => usersMap[id]).filter(Boolean);

  const startPolling = useCallback(
    (trackingRunId: string) => {
      if (pollRef.current) clearTimeout(pollRef.current);
      let attempts = 0;
      let nullCount = 0;
      const maxAttempts = 60;
      const maxNullAttempts = 20; // give up if no run record after ~60s (PDF generation can take 30s+)

      const poll = async () => {
        attempts++;
        try {
          const { data } = await client.query<OrScheduleReportSendStatusData, OrScheduleReportSendStatusVars>({
            query: OR_SCHEDULE_REPORT_SEND_STATUS,
            variables: { runId: trackingRunId },
            fetchPolicy: 'network-only',
          });
          const status = data?.orScheduleReportSendStatus;
          if (status) {
            setSendStatus(status);
            if (status.status !== 'running') {
              pollRef.current = null;
              setSending(false);
              if (status.status === 'success') {
                toaster.success(`Report delivered: ${status.successCount}/${status.totalRecipients} recipients`);
              } else {
                toaster.error(
                  `Report ${status.status}: ${status.errorMessage || `${status.successCount}/${status.totalRecipients} recipients`}`
                );
              }
              return;
            }
          } else {
            nullCount++;
            if (nullCount >= maxNullAttempts) {
              pollRef.current = null;
              setSending(false);
              toaster.error(
                'Report send failed: no run record found. The Lambda may have crashed before starting — check CloudWatch logs.'
              );
              return;
            }
          }
        } catch (err) {
          console.error('Report send status poll error:', err);
          nullCount++;
          if (nullCount >= maxNullAttempts || attempts >= maxAttempts) {
            pollRef.current = null;
            setSending(false);
            toaster.error(`Report send status polling failed: ${err instanceof Error ? err.message : 'unknown error'}`);
            return;
          }
        }
        pollRef.current = setTimeout(poll, 3000);
      };

      poll();
    },
    [client, toaster]
  );

  const handleSend = useCallback(async () => {
    setSending(true);
    setSendStatus(null);
    setRunId(null);
    try {
      const variables: SendOrScheduleReportVars = {
        reportDate,
      };
      if (recipientMode === 'custom') {
        variables.recipientUserIds = customRecipientIds;
      }

      const result = await client.mutate<SendOrScheduleReportData, SendOrScheduleReportVars>({
        mutation: SEND_OR_SCHEDULE_REPORT,
        variables,
      });

      const data = result.data?.sendOrScheduleReport;
      const needsPolling = data?.message?.includes('Poll orScheduleReportSendStatus');

      if (data?.success) {
        if (needsPolling && data.runId) {
          setRunId(data.runId);
          toaster.success('Report sending started');
          startPolling(data.runId);
        } else {
          setSending(false);
          toaster.success('Report sent');
          if (data.runId) {
            setRunId(data.runId);
            startPolling(data.runId);
          }
        }
      } else {
        setSending(false);
        toaster.error(`Report failed: ${data?.message ?? 'Unknown error'}`);
      }
    } catch (e: any) {
      setSending(false);
      toaster.error(`Report send failed: ${e.message}`);
    }
  }, [client, toaster, reportDate, recipientMode, customRecipientIds, startPolling]);

  const canSend = recipientMode === 'configured' ? configuredRecipientIds.length > 0 : customRecipientIds.length > 0;

  const isTerminal = sendStatus && sendStatus.status !== 'running';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send OR Schedule Report</DialogTitle>
      <DialogContent>
        <Box py={1}>
          <Typography variant="subtitle2">Report Details</Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }} mt={1}>
            <Chip label="Daily" size="small" color="primary" />
            <Typography variant="body2">{reportDate}</Typography>
          </Box>
        </Box>

        <Divider />

        <Box py={1}>
          <Typography variant="subtitle2">Recipients</Typography>
          <RadioGroup value={recipientMode} onChange={e => setRecipientMode(e.target.value as 'configured' | 'custom')}>
            <FormControlLabel
              value="configured"
              disabled={sending}
              control={<Radio size="small" color="primary" />}
              label={
                <Typography variant="body2">Use configured recipients ({configuredRecipientIds.length})</Typography>
              }
            />
            <FormControlLabel
              value="custom"
              disabled={sending}
              control={<Radio size="small" color="primary" />}
              label={<Typography variant="body2">Choose custom recipients</Typography>}
            />
          </RadioGroup>

          {recipientMode === 'configured' && (
            <Box display="flex" flexWrap="wrap" style={{ gap: 6 }} mt={1}>
              {configuredRecipientNames.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No configured recipients for daily reports.
                </Typography>
              ) : (
                configuredRecipientNames.map((user: any) => (
                  <Chip key={user.id} label={`${user.name} <${user.email}>`} size="small" />
                ))
              )}
            </Box>
          )}

          {recipientMode === 'custom' && (
            <Box mt={1}>
              <ReportRecipients
                name="customRecipients"
                value={customRecipientIds}
                onChange={(ids: number[]) => setCustomRecipientIds(ids ?? [])}
                disabled={sending}
                showChannelBadges
                menuPortalTarget={document.body}
              />
            </Box>
          )}
        </Box>

        <Divider />

        <Box py={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={sending || !canSend}
            startIcon={sending ? <CircularProgress size={16} /> : <Send style={{ fontSize: 16 }} />}
          >
            {sending ? 'Sending...' : 'Send Report'}
          </Button>

          {sending && !sendStatus && runId && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                Waiting for Lambda to start...
              </Typography>
            </Box>
          )}
          {sending && sendStatus?.status === 'running' && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                Sending: {sendStatus.successCount}/{sendStatus.totalRecipients} ...
              </Typography>
            </Box>
          )}
          {isTerminal && sendStatus && (
            <Box mt={1}>
              <Typography variant="body2" style={{ color: sendStatus.status === 'success' ? '#10b981' : '#ef4444' }}>
                {sendStatus.status === 'success'
                  ? `Delivered: ${sendStatus.successCount}/${sendStatus.totalRecipients} recipients`
                  : sendStatus.status === 'partial_success'
                    ? `Partial: ${sendStatus.successCount}/${sendStatus.totalRecipients} recipients, ${sendStatus.failedCount} failed`
                    : `Failed: ${sendStatus.errorMessage || `${sendStatus.failedCount}/${sendStatus.totalRecipients} failed`}`}
                {sendStatus.durationMs != null && ` | ${(sendStatus.durationMs / 1000).toFixed(1)}s`}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SendOrScheduleReportDialog;
